import { useTranslate } from '@tolgee/react';
import { Tooltip } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import EnterprisePlanIcon from '../../../../assets/icons/EnterprisePlanIcon';
import theme from '../../../../assets/theme';
import { LicenseCoarseLevels } from '../../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import { useGetGeneratorsByIdV2 } from '../../../../hooks/useGetGeneratorsById';
import { useMetrics } from '../../../../hooks/useMetrics';
import { newRelicNoticeError } from '../../../../hooks/useNewRelic';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import { TestPrepAction, TestPrepState } from '../../../../hooks/useTestPrepState';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { Grade } from '../../../_molecules/SettingsModalDesigner/SettingsModalDesigner';
import ProgressBarDynamicLesson from '../components/ProgressBarDynamicLesson';
import { SubPageLayout } from '../CreatePage';
import { useGetLessonIds } from './TestPrep.Utils';
import { TestPrepFirstPage } from './TestPrepFirstPage';
import { TestPrepLastPage } from './TestPrepLastPage';

export function TestPrepTab({
  testPrepState,
  dispatch,
  preFillGrade,
}: {
  testPrepState: TestPrepState;
  dispatch: React.Dispatch<TestPrepAction>;
  preFillGrade?: Grade;
}) {
  const { userAccessBadge, isReady, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const isInUSA = userAccessBadge?.organization?.country === 'US';
  const {
    selectedTest,
    selectedQuestionType,
    lessonVariant,
    isUpload,
    selectedPage,
    hasUploadOptions,
  } = testPrepState;

  const { tenantId } = useRouterMatch();

  const allLessonIdsForSelectedTest = useGetLessonIds({
    testId: selectedTest,
  });

  const { generators, isFetchingGenerators } = useGetGeneratorsByIdV2(
    allLessonIdsForSelectedTest,
  );

  const availableLessonIds = useGetLessonIds({
    testId: selectedTest,
    questionTypeId: selectedQuestionType,
    lessonVariantId: lessonVariant,
    isUpload,
  });

  const selectedGenerator = useCallback(() => {
    if (availableLessonIds?.length === 1) {
      const foundGenerator = generators?.find(
        generator => generator.id === availableLessonIds[0],
      );

      if (foundGenerator === undefined) {
        newRelicNoticeError('No generator found for test prep', {
          lessonIds: availableLessonIds,
          selectedTest,
          selectedQuestionType,
          lessonVariant,
          isUpload,
          generatorIdsFound: generators?.map(g => g.id),
        });
        return undefined;
      }
      return foundGenerator;
    }

    return undefined;
  }, [
    generators,
    availableLessonIds,
    selectedTest,
    selectedQuestionType,
    lessonVariant,
    isUpload,
  ]);
  const isLargerThan600 = useBreakPoint(600);
  const [showLoader, setShowLoader] = useState(false);

  const formProgress = useMemo(() => {
    if (selectedPage === 1) {
      // No subject selected, progress is 0%
      return 10;
    }
    if (selectedPage === 2) {
      return 50;
    }
    if (selectedPage === 3) {
      // on the generate page progress is 100%
      return 100;
    }
    return 0;
  }, [selectedPage]);

  const handleIsUploadChange = (isUpload: boolean) => {
    dispatch({ type: 'SET_IS_UPLOAD', payload: isUpload });
  };

  const handleLessonPageChange = (page: number) => {
    dispatch({ type: 'SET_SELECTED_PAGE', payload: page });
  };

  const { t } = useTranslate();

  return (
    <>
      {!showLoader && isLargerThan600 && (
        <ProgressBarDynamicLesson progress={formProgress} numberOfSteps={3} />
      )}
      {paywallComponent}

      <>
        {/*Select subject*/}
        {selectedPage === 1 && (
          <SubPageLayout
            pageTitle={t('TestPrepTab.PageTitle', 'Generate Your Test Prep')}
          >
            <TestPrepFirstPage
              testPrepState={testPrepState}
              dispatch={dispatch}
              tenantId={tenantId}
              isUsTeacher={isInUSA}
              availableGenerators={generators}
              userAccessBadge={userAccessBadge}
              generators={generators}
              promptPaywall={promptPaywall}
              isReady={isReady}
            />
          </SubPageLayout>
        )}
      </>

      {selectedPage === 2 && (
        <SubPageLayout pageTitle="Choose Your Content">
          <SelectUploadOrGenerate
            handleIsUploadChange={handleIsUploadChange}
            handleSelectedPageChange={handleLessonPageChange}
            hasUploadOption={!!hasUploadOptions}
          />
        </SubPageLayout>
      )}

      {selectedPage === 3 && (
        <>
          {/*Generate the lesson form */}
          <SubPageLayout
            pageTitle={
              showLoader
                ? ''
                : t('TestPrepTab.PageTitle.AFewMoreDetails', 'A few more details')
            }
          >
            <TabContainer>
              <TestPrepLastPage
                preFillGrade={preFillGrade}
                availableGenerators={generators}
                availableLessonIds={availableLessonIds}
                dispatch={dispatch}
                isFetchingGenerators={isFetchingGenerators}
                isReady={isReady}
                promptPaywall={promptPaywall}
                selectedGenerator={selectedGenerator()}
                setShowLoader={setShowLoader}
                showLoader={showLoader}
                testPrepState={testPrepState}
                userAccessBadge={userAccessBadge}
              />
            </TabContainer>
          </SubPageLayout>
        </>
      )}
    </>
  );
}

const AnimatedButton = ({
  handleSelectedPageChange,
  handleSelectUpload,
  buttonText,
  isEnterprisePlan,
  disabled,
}: {
  handleSelectedPageChange: (page: number) => void;
  handleSelectUpload: () => void;
  buttonText: string;
  isEnterprisePlan?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();
  const { userAccessBadge, isReady, promptPaywall, paywallComponent } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  return (
    <div style={{ position: 'relative' }}>
      <Tooltip
        title={!!disabled ? t('Coming Soon!', 'Coming Soon!') : ''}
        mouseEnterDelay={0.5}
      >
        <CategoryButton
          type="primary"
          width="200px"
          $isComingSoon={!!disabled}
          onClick={() => {
            if (disabled) {
              metrics.logEvent('TestPrep.DisabledButtonClicked');
              return;
            }
            if (!isReady) return;
            if (
              isEnterprisePlan &&
              userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
            ) {
              promptPaywall({
                label: t('Upgrade account test prep', 'Upgrade account test prep'),
                description: t(
                  'Use my own materials for test prep',
                  'Use my own materials for test prep',
                ),
              });
              return;
            } else {
              metrics.logEvent('TestPrep.ContentTypeButton.Clicked', {
                buttonText: buttonText,
                isEnterprisePlan: isEnterprisePlan,
              });
              handleSelectedPageChange(3);
              handleSelectUpload();
            }
          }}
        >
          {buttonText}
        </CategoryButton>
      </Tooltip>
      {paywallComponent}
      {isEnterprisePlan && (
        <PositioningWrapper>
          <TestprepPlanIcon
            hasAccessToGenerator={
              isEnterprisePlan &&
              userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise
            }
            isEnterprisePlanGenerator={isEnterprisePlan}
            isPremiumPlanGenerator={false}
          />
        </PositioningWrapper>
      )}
    </div>
  );
};

export const TestprepPlanIcon = ({
  isPremiumPlanGenerator,
  isEnterprisePlanGenerator,
  hasAccessToGenerator,
}: {
  isPremiumPlanGenerator: boolean | null | undefined;
  isEnterprisePlanGenerator: boolean;
  hasAccessToGenerator: boolean | null | undefined;
}) => {
  const { t } = useTranslate();
  return (
    <Tooltip
      title={
        hasAccessToGenerator
          ? t(
              'As a Curipod school plan subscriber, you get this for free.',
              'As a Curipod school plan subscriber, you get this for free.',
            )
          : t(
              'Curipod school plan subscribers get this for free.',
              'Curipod school plan subscribers get this for free.',
            )
      }
      mouseEnterDelay={0.5}
    >
      <IconWrapper
        width="15px"
        height="15px"
        style={{
          color: hasAccessToGenerator ? theme.colors.black : theme.colors.yellow,
        }}
      >
        {(isEnterprisePlanGenerator || isPremiumPlanGenerator) && <EnterprisePlanIcon />}
      </IconWrapper>
    </Tooltip>
  );
};

const PositioningWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 17px;
`;

const CategoryButton = styled(Button)<{ $isComingSoon: boolean }>`
  color: ${theme.colors.black};
  background-color: ${theme.colors.paleBeige};
  box-shadow: ${theme.effects.magicFloatingShadow};
  border-radius: 30px;
  width: 250px;
  position: relative !important;

  span {
    font-size: 21px;
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.white};
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    color: ${theme.colors.black};
  }

  ${props =>
    props.$isComingSoon &&
    css`
      color: ${theme.colors.black};
      background-color: ${theme.colors.paleBeige};
      box-shadow: ${theme.effects.magicFloatingShadow};

      &:hover,
      &:focus,
      &:active {
        color: ${theme.colors.black};
        background-color: ${theme.colors.paleBeige};
        box-shadow: ${theme.effects.magicFloatingShadow};
      }
    `}

  @media (max-width: 700px) {
    width: 220px;
    span {
      font-size: 18px;
    }
  }
`;

const SelectUploadOrGenerate = ({
  handleSelectedPageChange,
  handleIsUploadChange,
  hasUploadOption,
}: {
  handleSelectedPageChange: (page: number) => void;
  handleIsUploadChange: (isUpload: boolean) => void;
  hasUploadOption: boolean;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();
  return (
    <FormContainer
      style={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 20,
        justifyContent: 'center',
      }}
    >
      <AnimatedButton
        buttonText={t('Use my materials', 'Use my materials')}
        handleSelectUpload={() => {
          metrics.logEvent('TestPrep.UploadOwnMaterials.Clicked');
          handleIsUploadChange(true);
        }}
        handleSelectedPageChange={handleSelectedPageChange}
        isEnterprisePlan={true}
        disabled={!hasUploadOption}
      />
      <AnimatedButton
        buttonText={t('Create with AI', 'Create with AI')}
        handleSelectUpload={() => {
          metrics.logEvent('TestPrep.CreateWithAI.Clicked');
          handleIsUploadChange(false);
        }}
        handleSelectedPageChange={handleSelectedPageChange}
      />
    </FormContainer>
  );
};

export const FormErrorText = styled.p`
  color: ${theme.colors.red};
  font-size: 14px;
  margin: 0;
  margin-top: 10px;
  text-align: center;
  background-color: ${theme.colors.veryLightRed};
  border-radius: 10px;
  padding: 10px;
`;

export const OrangeButton = styled(Button)<{ $disabled: boolean }>`
  color: ${theme.colors.white};
  background-color: ${theme.colors.orange};
  box-shadow: ${theme.effects.magicFloatingShadow};
  margin: auto;

  svg {
    height: 25px;
    width: 25px;
    margin-right: 0px;
  }

  ${props =>
    props.$disabled &&
    css`
      background-color: ${theme.colors.orangeDark};
      box-shadow: ${theme.effects.magicFloatingShadow};
      &:hover {
        background-color: ${theme.colors.orangeDark};
        box-shadow: ${theme.effects.magicFloatingShadow};
      }
    `}

  &:hover {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
  }

  &:focus,
  &:active {
    background-color: ${theme.colors.orangeDark};
    box-shadow: ${theme.effects.defaultShadow};
  }
`;

export const FormContainer = styled.div`
  padding-top: 10px;
  margin: 20px auto;
  width: 100%;
  max-width: 800px;
  display: grid;
  gap: 20px;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const TabContainer = styled.div`
  padding-top: 0px;
  margin: 0px auto 50px auto;
  width: 80%;
  max-width: 800px;
  display: grid;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }

  @media (max-width: 700px) {
    padding-top: 0px;
  }
`;
